import React, { useEffect, useRef, useState, useLayoutEffect } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { TrendingUp, TrendingDown, HelpCircle } from 'lucide-react';
import { LOOP_TONES } from './loopVisuals';

/**
 * Which way does this spiral run?
 *
 * The one question about a loop that the graph cannot answer. A ring of
 * `heikentää` edges dismantling something harmful is a good spiral; a ring of
 * `vahvistaa` edges compounding a disaster is a bad one, and the sign rule
 * calls both "reinforcing". Only somebody reading the words can say.
 *
 * Asked at the moment the loop is drawn, because that is when the person has
 * the whole chain in their head — and answerable later from the loop's own
 * card, because a room changes its mind. "En tiedä vielä" is a real answer: an
 * unjudged loop is drawn as unjudged rather than coloured as if somebody had
 * decided.
 */

const appear = keyframes`
  from { opacity: 0; transform: translate(-8px, -50%) scale(0.96); }
  to   { opacity: 1; transform: translate(0, -50%) scale(1); }
`;

const Catcher = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  background: transparent;
`;

const Card = styled.div`
  position: fixed;
  z-index: 1201;
  transform: translateY(-50%);
  animation: ${appear} 0.14s cubic-bezier(0.2, 0.9, 0.3, 1);
  padding: 6px;
  border-radius: 14px;
  background: #14141f;
  border: 1px solid rgba(255, 255, 255, 0.13);
  box-shadow: 0 14px 44px rgba(0, 0, 0, 0.6);
  display: flex;
  flex-direction: column;
  gap: 1px;
  width: 290px;
`;

const Choice = styled.button`
  display: flex;
  align-items: flex-start;
  gap: 11px;
  width: 100%;
  padding: 10px 13px;
  border-radius: 10px;
  color: #fff;
  cursor: pointer;
  background: transparent;
  border: 1px solid transparent;
  text-align: left;
  font-family: inherit;
  transition: background 0.12s ease, border-color 0.12s ease, transform 0.12s ease;

  .icon { flex-shrink: 0; width: 16px; height: 16px; margin-top: 2px; color: ${(p) => p.$color}; }
  .text { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
  .label { font-size: 0.94rem; font-weight: 600; }
  .sub { font-size: 0.76rem; line-height: 1.35; color: rgba(255, 255, 255, 0.45); }

  &:hover { background: rgba(255, 255, 255, 0.08); border-color: ${(p) => p.$color}; transform: translateX(2px); }
`;

const OPTIONS = [
  { key: 'up', icon: TrendingUp, label: 'Hyvä kierre', sub: 'Paranee joka kierroksella.' },
  { key: 'down', icon: TrendingDown, label: 'Noidankehä', sub: 'Pahenee joka kierroksella.' },
  { key: 'unknown', icon: HelpCircle, label: 'En tiedä vielä', sub: 'Jätä arvioimatta — voit päättää myöhemmin.' },
];

export default function LoopDirectionPopover({ anchor, onSelect, onClose }) {
  const cardRef = useRef(null);
  const [placement, setPlacement] = useState({ left: 0, top: 0 });

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useLayoutEffect(() => {
    if (!anchor || !cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const margin = 12;
    const gap = 24;
    const half = rect.height / 2;
    const fitsRight = anchor.x + gap + rect.width <= window.innerWidth - margin;
    const left = fitsRight ? anchor.x + gap : Math.max(margin, anchor.x - gap - rect.width);
    const top = Math.min(Math.max(anchor.y, half + margin), window.innerHeight - half - margin);
    setPlacement({ left, top });
  }, [anchor]);

  if (!anchor) return null;

  return (
    <>
      <Catcher onClick={onClose} />
      <Card ref={cardRef} style={{ left: placement.left, top: placement.top }} onClick={(e) => e.stopPropagation()}>
        {OPTIONS.map((o) => (
          <Choice key={o.key} type="button" $color={LOOP_TONES[o.key].color} onClick={() => onSelect?.(o.key)}>
            <o.icon className="icon" />
            <span className="text">
              <span className="label">{o.label}</span>
              <span className="sub">{o.sub}</span>
            </span>
          </Choice>
        ))}
      </Card>
    </>
  );
}
